import type { Deal, TabState, SCData } from '../types';
import { ALL_DATA } from '../data/deals';
import { median, avgArr } from './format';
import { validAges, isAged, splitSCNames } from './deals';

export function matchesCQFilter(d: Deal, s: TabState): boolean {
  if (s.fy === 'All' && s.q === 'All') return true;
  const yr = d.created_fy || '';
  const qt = d.created_q || '';
  if (s.fy !== 'All' && yr !== s.fy) return false;
  if (s.q !== 'All' && qt !== s.q) return false;
  return true;
}

export function getFiltered(tab: 'vp' | 'sd' | 'od', s: TabState, data: Deal[] = ALL_DATA): Deal[] {
  return data.filter(d => {
    if (s.r && s.r !== 'Global' && d.region !== s.r) return false;
    if (s.sub && s.sub !== 'All' && d.sub_region !== s.sub) return false;
    if (tab === 'vp' && s.status && s.status !== 'All' && d.status !== s.status) return false;
    if (!matchesCQFilter(d, s)) return false;
    return true;
  });
}

export function buildSCList(region: string, sub: string, cqState: TabState, data: Deal[] = ALL_DATA): SCData[] {
  const scMap: Record<string, { deals: Deal[]; closedDeals: Deal[]; onhold: number; cancelled: number }> = {};

  data.forEach(d => {
    if (region !== 'Global' && d.region !== region) return;
    if (sub && sub !== 'All' && d.sub_region !== sub) return;
    if (!matchesCQFilter(d, cqState)) return;

    for (const scKey of splitSCNames(d.sc)) {
      if (!scMap[scKey]) scMap[scKey] = { deals: [], closedDeals: [], onhold: 0, cancelled: 0 };
      if (d.status === 'In Progress') scMap[scKey].deals.push(d);
      else if (d.status === 'Closed') scMap[scKey].closedDeals.push(d);
      else if (d.status === 'On Hold') scMap[scKey].onhold++;
      else if (d.status === 'Cancelled') scMap[scKey].cancelled++;
    }
  });

  return Object.entries(scMap)
    .map(([sc, v]) => {
      const ages = validAges(v.deals);
      const aged = v.deals.filter(d => isAged(d));
      const gnarr = v.deals.reduce((s, d) => s + d.gnarr, 0);
      return {
        n: sc,
        ip: v.deals.length,
        avgAge: avgArr(ages),
        med: median(ages),
        gnarr,
        aged: aged.length,
        riskGnarr: aged.reduce((s, d) => s + d.gnarr, 0),
        deals: v.deals,
        closedDeals: v.closedDeals,
        closed: v.closedDeals.length,
        onhold: v.onhold,
      };
    })
    .sort((a, b) => (b.ip + b.closed + b.onhold) - (a.ip + a.closed + a.onhold));
}

export function getFYOptions(data: Deal[] = ALL_DATA): string[] {
  const fySet = new Set(data.map(d => d.created_fy).filter(Boolean));
  return ['All', ...Array.from(fySet).sort()];
}

// Most recent fiscal year present in the data, or null when there's no year data yet
// (e.g. before live data has loaded). Used to auto-select the FY filter's default value.
export function getLatestFY(data: Deal[] = ALL_DATA): string | null {
  const years = getFYOptions(data).filter(y => y !== 'All');
  return years.length > 0 ? years[years.length - 1] : null;
}

export const PRODUCT_BUCKETS = [
  { label: 'AEM Assets', test: (p: string) => /(aem.*assets|assets.*aem)/i.test(p) },
  { label: 'Analytics', test: (p: string) => /(d&i.*analytics|analytics)/i.test(p) && !/cja/i.test(p) },
  { label: 'CJA', test: (p: string) => /cja/i.test(p) },
  { label: 'Campaign', test: (p: string) => /campaign/i.test(p) },
  { label: 'Commerce', test: (p: string) => /commerce/i.test(p) },
  { label: 'Marketo', test: (p: string) => /(marketo|measure)/i.test(p) },
  { label: 'Target', test: (p: string) => /target/i.test(p) },
  { label: 'Workfront', test: (p: string) => /(workfront|wkft)/i.test(p) },
];

export function bucketProducts(productsStr: string): Set<string> {
  const bucketHits = new Set<string>();
  const parts = productsStr.split(',').map(s => s.trim());
  for (const part of parts) {
    let matched = false;
    for (const b of PRODUCT_BUCKETS) {
      if (b.test(part)) { bucketHits.add(b.label); matched = true; }
    }
    if (!matched) bucketHits.add('Other');
  }
  return bucketHits;
}
